<template>
    <b-modal
        visible
        id="check-in-result-modal-v2"
        size="lg"
        hide-footer
        hide-header
        no-close-on-backdrop
        no-close-on-esc
    >
        <c-loader :loader="isLoading" />
        <template v-if="!isLoading">
            <template id="modal-title">
                <div id="header">
                    <span class="title">{{ $t('performCheckIn') }}</span>
                    <i class="close-button fas fa-times" @click="closeModal" />
                </div>
            </template>
            <b-row class="form-column">
                <b-col class="cols-12 last-checkin-container">
                    <h3>{{ result.title }}</h3>
                    <b-row>
                        <b-col class="cols-12 last-checked">
                            <span>{{ result.last_checkin ? $tc('lastCheckIn', 1) : $tc('lastCheckIn', 2) }}</span>
                            <b-button
                                v-if="result.last_checkin"
                                class="last-checkin-icon"
                                variant="outline-primary"
                                id="btn-strategy-last-checkin-result"
                                :disabled="validationDisabled"
                            >
                                <i class="far fa-calendar-check"></i>
                            </b-button>
                            <b-popover
                                v-if="result.last_checkin"
                                target="btn-strategy-last-checkin-result"
                                triggers="hover"
                                placement="top"
                            >
                                <template #title>{{ $t('summary') }}</template>
                                <div>
                                    <p>
                                        {{ $t('value') }}
                                        <span class="text-muted">{{ valueCheckInView }}</span>
                                    </p>
                                    <p>
                                        {{ $tc('generalComments', 2) }}
                                        <span
                                            class="text-muted ml-2"
                                            v-html="result.last_checkin.comments ? result.last_checkin.comments : ''"
                                        ></span>
                                    </p>
                                    <p>
                                        {{ $tc('difficultiesFound', 2) }}
                                        <span
                                            class="text-muted"
                                            v-html="
                                                result.last_checkin.difficulties ? result.last_checkin.difficulties : ''
                                            "
                                        ></span>
                                    </p>
                                </div>
                            </b-popover>
                        </b-col>
                        <b-col class="last-checked-meta cols-12">
                            <p>
                                {{ $t('target') }}
                                <span class="text-muted">{{ totalCheckInView }}</span>
                            </p>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
            <b-row class="mt-4">
                <b-col class="cols-12">
                    <div class="binary-status-content" v-if="result.metric === 'binary'">
                        <span gray>Status: </span>
                        <b-button-group size="sm">
                            <b-button
                                id="btn-strategy-select-status-modal-checkin"
                                v-for="item in optionsStatus"
                                :key="item.value"
                                :ref="item.value"
                                @click="buttonSelectedStatus(item.value)"
                                variant="outline-primary"
                                >{{ item.text }}
                            </b-button>
                        </b-button-group>
                        <div v-if="validationStatus" class="advertence">*{{ $t('validations.statusIsRequired') }}*</div>
                    </div>
                    <div v-else>
                        <span gray>{{ $t('currentValue') }}</span>
                        <b-form-input
                            type="number"
                            v-model="checkinValue"
                            :state="validationFieldValue"
                            required
                        ></b-form-input>
                    </div>
                </b-col>
                <b-col class="cols-12 binary-status-emoji">
                    <span gray>{{ $t('howAmIFeeling') }}</span>
                    <MenuHumor @valueSelected="valueSelectedHumor" />
                </b-col>
            </b-row>
            <b-row class="description-container">
                <b-col class="cols-12">
                    <span gray>{{ $tc('generalComments', 1) }}</span>
                    <Description @change-value="commentsUpdate" :value="comments" />
                </b-col>
                <b-col class="cols-12">
                    <span gray>{{ $tc('difficultiesFound', 1) }}</span>
                    <Description @change-value="difficultiesUpdate" :value="difficulties" />
                </b-col>
            </b-row>
            <b-row>
                <b-col class="button-submit">
                    <b-button id="btn-strategy-submitted-checkin" @click="onSubmittedCheckin" variant="primary"
                        >Check-in
                    </b-button>
                </b-col>
            </b-row>
        </template>
    </b-modal>
</template>
<script>
import api from '@/services/api'
import { success, danger } from '@/lib/notification'
import Description from '@/views/components/fields/Description'
import { MenuHumor } from './components'
import CLoader from '@/views/components/loader'

export default {
    name: 'NewCheckinResult',
    components: {
        Description,
        MenuHumor,
        CLoader
    },
    data() {
        return {
            isSubmitting: false,
            result: null,
            checkinValue: null,
            satisfaction: null,
            comments: null,
            difficulties: null,
            validationFieldValue: null,
            validationStatus: false,
            selectedStatus: null,
            status: null,
            optionsStatus: [
                {
                    text: this.$t('milestone.notStarted'),
                    value: 'not_started',
                },
                {
                    text: this.$t('milestone.inProgress'),
                    value: 'in_progress',
                },
                {
                    text: this.$t('milestone.done'),
                    value: 'done',
                },
            ],
        }
    },
    computed: {
        objectiveId() {
            return this.$route.params.objectiveId
        },
        resultId() {
            return this.$route.params.resultId
        },
        validationDisabled() {
            return !this.result.last_checkin
        },
        valueCheckInView() {
            let value = this.result.last_checkin.value.toFixed(2).toString().replace('.', ',')
            if (this.result.last_checkin && this.result.metric === 'percentage') return `${value} %`
            if (this.result.last_checkin && this.result.metric === 'monetary') return `$ ${value}`
            if (this.result.last_checkin) return `${this.result.last_checkin.value}`
            return this.$t('validations.thisFieldIsRequired')
        },
        totalCheckInView() {
            let value = this.result.target_value.toFixed(2).toString().replace('.', ',')
            if (this.result.metric === 'percentage') return `${value} %`
            if (this.result.metric === 'monetary') return `$ ${value}`
            if (this.result.metric === 'binary' || this.result.metric === 'numeric')
                return `${this.result.target_value}`
            return this.$t('validations.thisFieldIsRequired')
        },
        isLoading() {
            return this.result ? false : true
        }
    },
    mounted() {
        this.getResult()
    },
    methods: {
        async getResult() {
            return await api
                .get(`objectives/${this.objectiveId}/keyresults/${this.resultId}?with=last_checkin`)
                .then(({ data }) => {
                    this.result = data.data
                })
        },
        valueSelectedHumor(e) {
            this.satisfaction = e
        },
        onSubmittedCheckin() {
            if (this.checkinValue && this.result.metric !== 'binary') {
                return api
                    .post(`objectives/${this.objectiveId}/keyresults/${this.resultId}/checkins`, {
                        comments: this.comments,
                        difficulties: this.difficulties,
                        satisfaction: this.satisfaction,
                        value: this.checkinValue,
                    })
                    .then(() => {
                        this.showSuccessNotification()
                        this.closeModal()
                    })
                    .finally(() => {
                        this.$emit('onSubmittedCheckin')
                    })
            }
            if (this.status != null && this.result.metric === 'binary') {
                return api
                    .post(`objectives/${this.objectiveId}/keyresults/${this.resultId}/checkins`, {
                        comments: this.comments,
                        difficulties: this.difficulties,
                        satisfaction: this.satisfaction,
                        status: this.status,
                        value: this.status === 'done' ? 1 : 0,
                    })
                    .then(() => {
                        this.showSuccessNotification()
                        this.closeModal()
                    })
                    .finally(() => {
                        this.$emit('onSubmittedCheckin')
                    })
            }
            if (!this.checkinValue && this.result.metric !== 'binary') {
                danger(this.$t('validations.currentValueIsRequired'))
                this.validationFieldValue = false
            }
            if (this.status == null && this.result.metric === 'binary') {
                danger(this.$t('validations.statusIsRequired'))
                return (this.validationStatus = true)
            }
        },
        buttonSelectedStatus(value) {
            this.status = value
            this.validationStatus = false
            this.$refs[value][0].classList.add('button-checked')
            const list = Object.keys(this.$refs)
            list.forEach((el) => {
                if (value !== el) {
                    this.$refs[el][0].classList.remove('button-checked')
                }
            })
        },
        showSuccessNotification() {
            success(this.$t('checkInDone'))
        },
        closeModal() {
            this.$router.back()
        },
        commentsUpdate(e) {
            this.comments = e
        },
        difficultiesUpdate(e) {
            this.difficulties = e
        },
    },
    watch: {
        checkinValue(newValue) {
            if (newValue == '' || newValue == null) {
                return (this.validationFieldValue = false)
            }
            return (this.validationFieldValue = true)
        },
        status() {
            return (this.validationStatus = false)
        },
    },
    i18n: {
        messages: {
            'en-US': {
                performCheckIn: 'Perform check-in',
                lastCheckIn: 'Last check-in: | There are no previous check-ins.',
                summary: 'Summary:',
                value: 'Value:',
                generalComments: 'General comments | General comments:',
                difficultiesFound: 'Difficulties found | Difficulties found:',
                target: 'Target:',
                currentValue: 'Current value',
                howAmIFeeling: 'How am I feeling?',
                checkInDone: 'Check-in done.',
                milestone: {
                    notStarted: 'Not started',
                    inProgress: 'In progress',
                    done: 'Done',
                },
                validations: {
                    thisFieldIsRequired: 'This field is required.',
                    statusIsRequired: 'Status is required.',
                    currentValueIsRequired: 'Current value is required.',
                },
            },
            'es-ES': {
                performCheckIn: 'Hacer check-in',
                lastCheckIn: 'Ultimo check-in: | No hay check-ins previos.',
                summary: 'Resumen:',
                value: 'Valor:',
                generalComments: 'Comentarios generales | Comentarios generales:',
                difficultiesFound: 'Dificultades encontradas | Dificultades encontradas:',
                target: 'Meta:',
                currentValue: 'Valor actual',
                howAmIFeeling: '¿Cómo me siento?',
                checkInDone: 'Check-in acabado.',
                milestone: {
                    notStarted: 'No empezado',
                    inProgress: 'En progreso',
                    done: 'Acabado',
                },
                validations: {
                    thisFieldIsRequired: 'Este campo es requerido.',
                    statusIsRequired: 'El estado es obligatorio.',
                    currentValueIsRequired: 'El valor actual es obligatorio.',
                },
            },
            'pt-BR': {
                performCheckIn: 'Fazer check-in',
                lastCheckIn: 'Último check-in: | Nenhum check-in foi realizado.',
                summary: 'Resumo:',
                value: 'Valor:',
                generalComments: 'Comentários gerais | Comentários gerais:',
                difficultiesFound: 'Dificuldades encontradas | Dificuldades encontradas:',
                target: 'Meta:',
                currentValue: 'Valor atual',
                howAmIFeeling: 'Como estou me sentindo?',
                checkInDone: 'Check-in realizado.',
                milestone: {
                    notStarted: 'Não iniciado',
                    inProgress: 'Em progresso',
                    done: 'Finalizado',
                },
                validations: {
                    thisFieldIsRequired: 'Este campo é obrigatório.',
                    statusIsRequired: 'O status é obrigatório.',
                    currentValueIsRequired: 'O valor atual é obrigatório.',
                },
            },
        },
    },
}
</script>

<style lang="scss" scoped>
[gray] {
    color: #656565;
    font-weight: bold;
}
.button-checked {
    background: #009ada !important;
    color: #fff !important;
}
#header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    .title {
        font-size: 1.5rem;
        font-weight: 700;
        text-align: left;
        color: #009ada;
    }

    .close-button {
        color: #3b3f4e;
        cursor: pointer;
        font-size: 18px;
    }
}

::v-deep .modal-lg {
    max-width: 900px;
}

.binary-status-content {
    display: flex;
    flex-direction: column;
}

.binary-status-emoji {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
}
.advertence {
    color: #de5b5b;
    margin-top: 0.2rem;
    font-size: 0.8rem;
    font-weight: bold;
}
.descriptions-column {
    padding: 1rem;
}

.form-column {
    color: #656565;
    font-weight: bold;
    border-top: 0.5px solid #bec0c0;
    padding: 1rem 0;
}
.last-checkin-container {
    display: flex;
    flex-direction: column;
    row-gap: 0.5rem;
    h3 {
        color: #000;
    }
    div {
        display: grid;
        grid-template-columns: 1fr 1fr;
        align-items: center;
    }
    div > .last-checked {
        display: flex;
        align-items: center;
    }
    div > .last-checked-meta {
        display: flex;
        align-items: center;
        p {
            margin: 0;
        }
    }
    & > div > div > span:first-child {
        font-size: 1.1rem;
    }
    .last-checkin-content {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
    }
    .last-checkin-icon {
        padding: 0.2rem 0.5rem;
        border-radius: 50%;
        margin-left: 0.5rem;
    }
}
.description-container {
    margin-top: 1rem;
}
.button-submit {
    display: flex;
    justify-content: flex-end;
}
</style>
